import { useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { AkselLocale, Combobox } from 'components/form/aksel';

const valueHandler = {
	get(value, options, multiple) {
		if (!value) {
			return multiple ? [] : null;
		}

		if (!multiple) {
			return (
				options.find(opt => opt.id === value || opt.value === value) ??
				null
			);
		}

		if (Array.isArray(value)) {
			return value
				.map(item =>
					options.find(opt => opt.id === item || opt.value === item)
				)
				.filter(Boolean);
		}

		return [
			options.find(opt => opt.id === value || opt.value === value),
		].filter(Boolean);
	},

	set(value, multiple) {
		if (!value) {
			return multiple ? [] : null;
		}

		if (!multiple) {
			return value?.id ?? value?.value ?? null;
		}

		if (Array.isArray(value)) {
			return value.map(item => item?.id ?? item?.value).filter(Boolean);
		}

		return value?.id || value?.value ? [value.id ?? value.value] : [];
	},
};

function idOf(option) {
	return String(option?.id ?? option?.value ?? '');
}

export default function AutoComplete({
	name,
	label,
	defaultValue = null,
	options = [],
	loading = false,
	required = false,
	multiple = false,
	disabled = false,
	rules = {},
	helperText = undefined,
	getOptionDisabled = undefined,
	// eslint-disable-next-line no-unused-vars -- Unused legacy prop.
	renderOption = undefined,
	// eslint-disable-next-line no-unused-vars -- Unused legacy prop.
	getOptionKey = undefined,
	// eslint-disable-next-line no-unused-vars -- Unused legacy prop.
	noOptionsText = undefined,
	...rest
}) {
	const { control, setValue, getValues } = useFormContext();

	const isBlocked = option =>
		!!option?.disabled ||
		(getOptionDisabled ? getOptionDisabled(option) : false);

	const listed = options.map(option => {
		const hinted = option.hint
			? `${option.label} (${option.hint})`
			: option.label;

		return {
			value: idOf(option),
			label: isBlocked(option) ? `${hinted} – ikke tilgjengelig` : hinted,
		};
	});

	const getValue = useCallback(
		value => valueHandler.get(value, options, multiple),
		[options, multiple]
	);
	const setFieldValue = useCallback(
		value => valueHandler.set(value, multiple),
		[multiple]
	);

	/* Apply late defaults to empty fields; Controller reads defaultValue only on mount. */
	useEffect(() => {
		const normalizedDefault = !defaultValue && multiple ? [] : defaultValue;

		if (normalizedDefault === undefined) return;

		const current = getValues(name);

		const isEmpty = multiple
			? !Array.isArray(current) || current.length === 0
			: current === undefined || current === null || current === '';

		if (!isEmpty) return;

		setValue(name, normalizedDefault, {
			shouldDirty: false,
			shouldValidate: false,
		});
	}, [defaultValue, multiple, name, getValues, setValue]);

	const requiredInRules = required
		? { required: 'Dette feltet er påkrevd' }
		: {};

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={!defaultValue && multiple ? [] : defaultValue}
			rules={{ ...requiredInRules, ...rules }}
			render={({
				field: { onChange, value, ...fieldProps },
				fieldState: { error },
			}) => {
				const picked = []
					.concat(getValue(value) ?? [])
					.filter(Boolean)
					.map(option => {
						const printed = idOf(option);
						return (
							listed.find(item => item.value === printed) ?? {
								value: printed,
								label: option.label,
							}
						);
					});

				const toggle = (printed, isSelected) => {
					const option = options.find(item => idOf(item) === printed);

					if (option && isBlocked(option)) return;

					if (!multiple) {
						onChange(isSelected ? setFieldValue(option) : null);
						return;
					}

					const held = Array.isArray(value) ? value : [];
					const id = option?.id ?? option?.value ?? printed;

					onChange(
						isSelected
							? [...held.filter(item => item !== id), id]
							: held.filter(item => item !== id)
					);
				};

				return (
					<AkselLocale>
						<Combobox
							label={label}
							options={listed}
							selectedOptions={picked}
							isMultiSelect={multiple}
							isLoading={loading}
							disabled={disabled}
							{...(helperText === undefined
								? {}
								: { description: helperText })}
							{...(error?.message === undefined
								? {}
								: { error: error.message })}
							onToggleSelected={toggle}
							{...fieldProps}
							{...rest}
						/>
					</AkselLocale>
				);
			}}
		/>
	);
}
